import { useEffect, useState } from "react";
import { api, API_BASE, getToken, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, FileText, Download, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { InfoTip } from "@/components/app/InfoTip";

export default function Reports() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const [uploads, setUploads] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!selectedBrandId) return;
    api.get(`/uploads?brand_id=${selectedBrandId}`).then((r) => setUploads((r.data || []).filter((u) => u.upload_status === "Processed")));
  }, [selectedBrandId]);

  // Load any persisted narrative for the latest upload.
  useEffect(() => {
    if (!selectedBrandId) { setNarrative(null); return; }
    setNarrativeLoading(true);
    api.get(`/recommendations/narrative?brand_id=${selectedBrandId}`)
      .then((r) => setNarrative(r.data || null))
      .catch(() => setNarrative(null))
      .finally(() => setNarrativeLoading(false));
  }, [selectedBrandId]);

  const generateNarrative = async () => {
    if (!selectedBrandId) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/recommendations/narrative?brand_id=${selectedBrandId}`);
      setNarrative(data);
      if (data.error) {
        toast.warning(`Used fallback summary — ${data.error.slice(0, 120)}`);
      } else {
        toast.success(`Memo generated · ${data.model || "AI"}`);
      }
    } catch (e) {
      toast.error(extractError(e, "Failed to generate memo"));
    } finally { setGenerating(false); }
  };

  const download = async (kind, uploadId, weekEnd) => {
    setDownloading(`${kind}-${uploadId || "latest"}`);
    try {
      const url = `${API_BASE}/exports/${kind}?brand_id=${selectedBrandId}${uploadId ? `&upload_id=${uploadId}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext = kind === "excel" ? "xlsx" : "pdf";
      const name = `MarketplaceOS_${selectedBrand?.brand_name || "report"}_${weekEnd || "latest"}.${ext}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Failed to download: ${e.message}`);
    } finally { setDownloading(null); }
  };

  const latest = uploads[0];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Roadmap</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Reports & Exports</h1>
        <p className="mt-1 text-sm text-slate-500">Excel workbook for analyst deep-dive · PDF strategy report for management review.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="reports-grid">
        <Card
          icon={FileSpreadsheet}
          tone="emerald"
          title="Excel — Full data workbook"
          desc="6 sheets: Executive Summary · Search Terms · Bidding · Listing · Product Gaps · Execution Plan."
          disabled={!latest || downloading?.startsWith("excel")}
          loading={downloading?.startsWith("excel")}
          onClick={() => download("excel", null, latest?.week_end_date)}
          testid="report-excel-latest"
        />
        <Card
          icon={FileText}
          tone="rose"
          title="PDF — Weekly strategy report"
          desc="Cover · KPIs · Top opportunity keywords · Bidding actions · Product gaps · Execution checklist."
          disabled={!latest || downloading?.startsWith("pdf")}
          loading={downloading?.startsWith("pdf")}
          onClick={() => download("pdf", null, latest?.week_end_date)}
          testid="report-pdf-latest"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="ai-narrative-card">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-violet-200 bg-violet-50 text-violet-700">
                <Sparkles className="h-4 w-4" />
              </span>
              <h3 className="text-base font-semibold text-slate-900 tracking-tight inline-flex items-center gap-1">AI weekly memo <InfoTip term="AI_MEMO" /></h3>
              {narrative?.model && narrative.markdown && (
                <span className="ml-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{narrative.model}</span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {latest
                ? <>One-paragraph executive memo for week <span className="font-semibold text-slate-700">{latest.week_start_date} → {latest.week_end_date}</span></>
                : "Upload a CSV to generate a memo."}
            </p>
          </div>
          <Button
            data-testid="generate-narrative-btn"
            disabled={!latest || generating}
            onClick={generateNarrative}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg gap-2 shrink-0">
            <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Generating…" : (narrative?.markdown ? "Regenerate" : "Generate memo")}
          </Button>
        </div>
        <div className="px-6 py-5 prose prose-slate max-w-none prose-sm prose-headings:tracking-tight prose-h3:text-base prose-p:leading-relaxed prose-li:my-0.5">
          {narrativeLoading ? (
            <p className="text-sm text-slate-500">Loading memo…</p>
          ) : narrative?.markdown ? (
            <ReactMarkdown>{narrative.markdown}</ReactMarkdown>
          ) : (
            <p className="text-sm text-slate-500">
              No memo yet. Click <span className="font-semibold text-slate-700">Generate memo</span> to create a brand-specific weekly narrative powered by an AI strategist.
            </p>
          )}
          {narrative?.error && narrative?.markdown && (
            <p className="mt-3 text-xs text-amber-600">⚠ {narrative.error}</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900 tracking-tight">Upload history</h3>
          <p className="text-xs text-slate-500 mt-0.5">Download a report for any past week.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Reporting week</th>
              <th className="px-4 py-3 text-left">Marketplace</th>
              <th className="px-4 py-3 text-left">File</th>
              <th className="px-4 py-3 text-right">Rows</th>
              <th className="px-4 py-3 text-right">Downloads</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 text-slate-900 font-semibold">{u.week_start_date} → {u.week_end_date}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{u.marketplace} · {u.country}</td>
                <td className="px-4 py-3 text-xs text-slate-600 max-w-[280px] truncate">
                  {(u.source_files && u.source_files.length > 1)
                    ? `${u.source_files.length} files · ${u.source_files.join(" · ")}`
                    : (u.file_name || "—")}
                </td>
                <td className="px-4 py-3 text-right text-xs font-mono text-slate-700">{u.processed_rows?.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => download("excel", u.id, u.week_end_date)} className="h-7 gap-1 text-xs" data-testid={`report-excel-${u.id}`}>
                      <Download className="h-3 w-3" /> Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => download("pdf", u.id, u.week_end_date)} className="h-7 gap-1 text-xs" data-testid={`report-pdf-${u.id}`}>
                      <Download className="h-3 w-3" /> PDF
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!uploads.length && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">No processed uploads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ icon: Icon, tone, title, desc, disabled, loading, onClick, testid }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    rose: "border-rose-200 bg-rose-50/40 text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-base font-semibold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-1 text-sm text-slate-500">{desc}</p>
      <Button data-testid={testid} disabled={disabled} onClick={onClick} className="mt-5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg gap-2">
        <Download className="h-4 w-4" /> {loading ? "Generating…" : "Download latest"}
      </Button>
    </div>
  );
}
